import { useEffect, useMemo, useState } from 'react';
import { Alert, Avatar, Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAnalytics } from '../../../../api/analytics';
import CustomCircularProgress from '../../../../components/ui/customCircularProgress/customCircularProgress.component';
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import StatCard from '../../../../components/ui/statCard/statCard.component';
import ServiceDistributionCharts from '../../../../components/service/serviceDistributionCharts/serviceDistributionCharts.component';
import { useHasPermission } from '../../../../contexts/auth';
import {
  BREAKDOWN_EQUIPMENT_ENDPOINT,
  BREAKDOWN_EQUIPMENT_USED_PARTS_ENDPOINT,
  BREAKDOWN_JOB_ISSUES_ENDPOINT,
  BREAKDOWN_JOB_SERVICES_ENDPOINT,
  BREAKDOWN_USED_PARTS_ENDPOINT,
} from '../../../../consts';
import { GeneralServiceStats } from '../../../../types/analytics';
import DateRangeFilter from '../../../../components/ui/dateRangeFilter/dateRangeFilter.component';
import { useDateRangeQueryParams } from '../../../../hooks/useDateRangeQueryParams';
import { buildUsedPartsBreakdownColumns, formatUsedPartsTotals } from '../../../../utils/partDisplay';
import { formatNumber, formatQuantity } from '../../../../utils';

interface TopEquipmentItem {
  id: number;
  name: string;
  // A count for the job issue/service breakdowns, but a summed UsedPart
  // quantity (a decimal) for the used parts one - so it goes through
  // formatQuantity rather than being printed raw.
  quantity: number;
}

// Chips cell shared by every "Top X" breakdown table on this page (used parts,
// job issues, job services) - each row's top_equipment array has the same shape.
const TopEquipmentCell = ({ items }: { items: TopEquipmentItem[] | undefined }) => {
  if (!items || items.length === 0) return <>-</>;
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        width: '100%',
        height: '100%',
        minHeight: 52,
      }}
    >
      {items.map((item) => (
        <Chip
          key={item.id}
          avatar={
            <Avatar
              sx={(theme) => ({
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(59, 130, 246, 0.28) !important'
                    : '#DBEAFE !important',
                color: theme.palette.mode === 'dark' ? '#BFDBFE !important' : '#1E40AF !important',
                width: '26px !important',
                height: '26px !important',
                fontSize: '0.85rem',
                fontWeight: 700,
              })}
            >
              {formatQuantity(item.quantity)}
            </Avatar>
          }
          label={item.name}
          size="medium"
          variant="outlined"
          sx={{ '& .MuiChip-label': { fontSize: '0.875rem' } }}
        />
      ))}
    </Box>
  );
};

export const GeneralStats = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewJobs = useHasPermission('service.view_job');
  const { loading, error, getGeneralServiceStats } = useAnalytics();
  const { dateRange, setDateRange } = useDateRangeQueryParams();
  const [stats, setStats] = useState<GeneralServiceStats | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      if (!canViewJobs) {
        return;
      }
      const data = await getGeneralServiceStats(dateRange);
      if (!cancelled) {
        setStats(data || null);
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [dateRange, canViewJobs, getGeneralServiceStats]);

  const scopeParams = useMemo(
    () => ({
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    }),
    [dateRange.start_date, dateRange.end_date]
  );
  const scopeKey = `${dateRange.start_date}-${dateRange.end_date}`;

  if (!canViewJobs) {
    return null;
  }

  const equipmentUsedPartsColumns: GridColDef[] = [
    { field: 'name', headerName: t('Equipment'), flex: 1, minWidth: 200 },
    {
      field: 'used_parts_value',
      headerName: t('Parts Value (zł)'),
      width: 150,
      valueGetter: (value) => formatNumber(value),
    },
    {
      field: 'used_parts_quantity',
      headerName: t('Parts Quantity'),
      width: 140,
      valueGetter: (value) => formatQuantity(value),
    },
  ];

  const topEquipmentColumn: GridColDef = {
    field: 'top_equipment',
    headerName: t('Top Equipment'),
    flex: 1.5,
    minWidth: 240,
    sortable: false,
    renderCell: (params) => <TopEquipmentCell items={params.row.top_equipment} />,
  };

  const usedPartColumns: GridColDef[] = [
    ...buildUsedPartsBreakdownColumns(t),
    topEquipmentColumn,
  ];

  const equipmentColumns: GridColDef[] = [
    { field: 'name', headerName: t('Equipment'), flex: 1, minWidth: 200 },
    { field: 'jobs_count', headerName: t('Jobs'), width: 120 },
  ];

  const jobIssueColumns: GridColDef[] = [
    { field: 'title', headerName: t('Issue'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
    topEquipmentColumn,
  ];

  const jobServiceColumns: GridColDef[] = [
    { field: 'title', headerName: t('Service'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
    topEquipmentColumn,
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {t('General Statistics')}
      </Typography>

      <Card>
        <CardContent>
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </CardContent>
      </Card>

      {loading && !stats && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CustomCircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">{t('There was an error, please review the form.')}</Alert>
      )}

      {!loading && !error && !stats && (
        <Alert severity="info">{t('No data available')}</Alert>
      )}

      {stats && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <StatCard label={t('Jobs in Selected Period')} value={stats.jobs_in_range} />
            <StatCard
              label={t('Used Parts')}
              value={formatUsedPartsTotals(stats.used_parts)}
            />
            <StatCard label={t('Job Issues')} value={stats.job_issues_count} />
            <StatCard label={t('Job Services')} value={stats.job_services_count} />
          </Box>

          <ServiceDistributionCharts
            stats={stats}
            loading={loading}
            hasColorEquipment={stats.has_color_equipment}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Equipment by Parts Cost')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_EQUIPMENT_USED_PARTS_ENDPOINT}
                  columns={equipmentUsedPartsColumns}
                  extraParams={scopeParams}
                  onRowClick={(id) => navigate(`/equipment-section/equipments/${id}`)}
                  dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Most Visited Equipment')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_EQUIPMENT_ENDPOINT}
                  columns={equipmentColumns}
                  extraParams={scopeParams}
                  onRowClick={(id) => navigate(`/equipment-section/equipments/${id}`)}
                  dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
          </Box>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('Top Used Parts')}
              </Typography>
              <CustomDataGrid
                key={scopeKey}
                url={BREAKDOWN_USED_PARTS_ENDPOINT}
                columns={usedPartColumns}
                extraParams={scopeParams}
                dataGridProps={{ autoHeight: true, getRowHeight: () => 'auto', pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
              />
            </CardContent>
          </Card>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Top Job Issues')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_JOB_ISSUES_ENDPOINT}
                  columns={jobIssueColumns}
                  extraParams={scopeParams}
                  dataGridProps={{ autoHeight: true, getRowHeight: () => 'auto', pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Top Job Services')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_JOB_SERVICES_ENDPOINT}
                  columns={jobServiceColumns}
                  extraParams={scopeParams}
                  dataGridProps={{ autoHeight: true, getRowHeight: () => 'auto', pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
};
